/**
 * Company sign-up, e-mail verification, and sign-in.
 *
 * An account exists only so a company can manage what it has posted and paid
 * for; browsing and applying never require one. Sign-up is followed by a code
 * sent to the address given, and confirming it returns to whatever the visitor
 * was trying to do.
 */

import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useState, type FormEvent, type ReactNode } from 'react';

import { LISTING_LIFETIME_DAYS, MINIMUM_FIRST_BID_CENTS } from '@bidtohire/shared';

import { Money } from '../components/Money';

import { TextField } from '../components/Fields';
import { errorMessage, ErrorNotice, SuccessNotice } from '../components/Status';
import { MoneyWhereMouthIs } from '../components/ui';
import { useAuth } from '../lib/auth-context';
import { useConfigState } from '../lib/config';

type Mode = 'signin' | 'signup' | 'confirm' | 'forgot' | 'reset';

const PASSWORD_HINT =
  'At least twelve characters, including upper case, lower case, and a number.';

const TITLES: Record<Mode, string> = {
  signup: 'Create a company account',
  confirm: 'Confirm your email address',
  signin: 'Sign in',
  forgot: 'Reset your password',
  reset: 'Choose a new password',
};

export function AuthPage(): ReactNode {
  const auth = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const configState = useConfigState();

  const next = params.get('next') ?? '/dashboard';
  const [mode, setMode] = useState<Mode>(params.get('mode') === 'signup' ? 'signup' : 'signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [notice, setNotice] = useState<string | undefined>(undefined);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(undefined);
    setNotice(undefined);
    setBusy(true);
    try {
      if (mode === 'signup') {
        const result = await auth.signUp(email, password);
        if (result.confirmationRequired) {
          setMode('confirm');
          setNotice(`Confirmation code sent to ${email}.`);
        } else {
          await auth.signIn(email, password);
          void navigate(next, { replace: true });
        }
      } else if (mode === 'confirm') {
        await auth.confirm(email, code);
        await auth.signIn(email, password);
        void navigate(next, { replace: true });
      } else if (mode === 'forgot') {
        await auth.requestPasswordReset(email);
        setMode('reset');
        // Conditional on purpose. The pool is configured to hide whether an
        // address has an account, and confirming it here would undo that.
        setNotice(`If an account exists for ${email}, a reset code is on its way.`);
      } else if (mode === 'reset') {
        await auth.resetPassword(email, code, password);
        await auth.signIn(email, password);
        void navigate(next, { replace: true });
      } else {
        await auth.signIn(email, password);
        void navigate(next, { replace: true });
      }
    } catch (caught) {
      setError(errorMessage(caught));
      const code = (caught as { code?: string } | null)?.code;
      if (code === 'UserNotConfirmedException') setMode('confirm');
      // A pool that demands a reset leaves no other way forward, so the screen
      // that offers one is where the attempt lands.
      if (code === 'PasswordResetRequiredException') setMode('forgot');
    } finally {
      setBusy(false);
    }
  }

  async function resend(): Promise<void> {
    setError(undefined);
    try {
      if (mode === 'reset') {
        await auth.requestPasswordReset(email);
        setNotice(`If an account exists for ${email}, another reset code is on its way.`);
        return;
      }
      await auth.resendCode(email);
      setNotice(`New confirmation code sent to ${email}.`);
    } catch (caught) {
      setError(errorMessage(caught));
    }
  }

  if (configState.failed) {
    return (
      <div className="inner" style={{ paddingTop: 60, paddingBottom: 80, maxWidth: 620 }}>
        <ErrorNotice
          error={
            new Error(
              'Accounts are temporarily unavailable. Browsing and applying still work.',
            )
          }
        />
      </div>
    );
  }

  return (
    <div className="inner">
      <div className="authpage">
        <div className="aside">
          <span className="eyebrow">For employers</span>
          <h2 style={{ marginTop: 12, fontSize: 34, letterSpacing: '-0.034em', maxWidth: '16ch' }}>
            <MoneyWhereMouthIs />
          </h2>
          <p className="lede" style={{ marginTop: 16, maxWidth: '46ch', fontSize: 16 }}>
            An account is for posting listings and managing what you have paid for.{' '}
            <Money cents={MINIMUM_FIRST_BID_CENTS} /> reserves a listing and buys its position;
            paste your posting afterwards and we fill in the details. Listings run{' '}
            {LISTING_LIFETIME_DAYS} days; renewal is free.
          </p>
          <p className="small muted" style={{ marginTop: 20 }}>
            Looking for a role instead? <Link to="/">Browse the leaderboard</Link> — no account
            required.
          </p>
        </div>

        <div className="card is-raised authcard">
          <h1>{TITLES[mode]}</h1>
          <p className="sub">
            {mode === 'signup'
              ? 'One account per company. Your profile and first listing come next.'
              : mode === 'confirm'
                ? 'Six digits, from the email we sent.'
                : mode === 'forgot'
                  ? 'We will email a six-digit code to the address on the account.'
                  : mode === 'reset'
                    ? 'Six digits from that email, plus the password you want.'
                    : 'Your listings and their positions are in the dashboard.'}
          </p>

          {notice === undefined ? null : (
            <div style={{ marginTop: 18 }}>
              <SuccessNotice>{notice}</SuccessNotice>
            </div>
          )}
          {error === undefined ? null : (
            <div style={{ marginTop: 18 }}>
              <ErrorNotice error={new Error(error)} />
            </div>
          )}

          <form onSubmit={(event) => void submit(event)}>
            <TextField
              label="Work email address"
              type="email"
              value={email}
              onChange={setEmail}
              autoComplete="email"
              required
              disabled={mode === 'confirm' || mode === 'reset'}
            />

            {mode === 'confirm' || mode === 'reset' ? (
              <TextField
                label={mode === 'reset' ? 'Reset code' : 'Confirmation code'}
                value={code}
                onChange={setCode}
                inputMode="numeric"
                autoComplete="one-time-code"
                required
              />
            ) : null}

            {/* Confirming carries the password already typed at sign-up, and a
                reset request has nothing to authenticate with yet. */}
            {mode === 'confirm' || mode === 'forgot' ? null : (
              <TextField
                label={mode === 'reset' ? 'New password' : 'Password'}
                type="password"
                value={password}
                onChange={setPassword}
                autoComplete={
                  mode === 'signup' || mode === 'reset' ? 'new-password' : 'current-password'
                }
                hint={mode === 'signup' || mode === 'reset' ? PASSWORD_HINT : undefined}
                required
              />
            )}

            <button type="submit" className="cta" disabled={busy || !auth.isReady}>
              {busy
                ? 'Working…'
                : mode === 'signup'
                  ? 'Create account'
                  : mode === 'confirm'
                    ? 'Confirm and sign in'
                    : mode === 'forgot'
                      ? 'Send reset code'
                      : mode === 'reset'
                        ? 'Set password and sign in'
                        : 'Sign in'}
            </button>

            {mode === 'signup' ? (
              <p className="small muted" style={{ marginTop: 14, textAlign: 'center' }}>
                By creating an account you agree to the <Link to="/terms">Terms</Link> and the{' '}
                <Link to="/privacy">Privacy Policy</Link>.
              </p>
            ) : null}

            <div className="row" style={{ marginTop: 16, justifyContent: 'center', gap: 16 }}>
              {mode === 'confirm' || mode === 'reset' ? (
                <button type="button" className="linkbtn small" onClick={() => void resend()}>
                  Send a new code
                </button>
              ) : (
                <button
                  type="button"
                  className="linkbtn small"
                  onClick={() => {
                    setError(undefined);
                    setNotice(undefined);
                    setMode(mode === 'signup' ? 'signin' : 'signup');
                  }}
                >
                  {mode === 'signup' ? 'I already have an account' : 'Create a company account'}
                </button>
              )}

              {/* An account holds listings paid for with money that is never
                  refunded, so a forgotten password must have a way back that is
                  not "make a second account" — which the page rules out anyway. */}
              {mode === 'signin' ? (
                <button
                  type="button"
                  className="linkbtn small"
                  onClick={() => {
                    setError(undefined);
                    setNotice(undefined);
                    setMode('forgot');
                  }}
                >
                  Forgot your password?
                </button>
              ) : null}

              {mode === 'forgot' || mode === 'reset' ? (
                <button
                  type="button"
                  className="linkbtn small"
                  onClick={() => {
                    setError(undefined);
                    setNotice(undefined);
                    setMode('signin');
                  }}
                >
                  Back to sign in
                </button>
              ) : null}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
